import { useTranslation } from 'react-i18next'
import { Pencil } from '@digital-twins/design-system/icons'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import { localizedCountryName } from '@/lib/utils/localized-country-name'
import { SOCIAL_PLATFORM_LABELS } from './socialFormat'
import { socialDepthOptionLabel } from './SocialTrackerScheduleFields'
import {
  brandSearchQueries,
  channelBrandNames,
  communityNames,
  SOCIAL_UNIVERSE_PLATFORM_ORDER,
} from './socialUniverseForm'

import { SocialBrandInput, SocialUniverseChannel } from '@digital-twins/shared-types'
import { TFunction } from 'i18next'
import { UniverseFormState, UniverseStep } from './socialUniverseForm'

export interface SocialUniverseReviewProps {
  form: UniverseFormState
  /** The saved Topic's channels (empty on create): their `brandCoverage` gives each brand's coverage line. */
  channels: Array<SocialUniverseChannel>
  languageLabel: (code: string) => string
  onEdit: (step: UniverseStep) => void
}

/**
 * One brand's coverage over the saved channels (A10): per channel its routes
 * and accepted posts, each skipped route with its typed reason, and the days
 * read where brands on that channel read different windows. Null when no
 * channel states coverage for the brand.
 */
export function socialBrandCoverageLine(
  brand: SocialBrandInput,
  channels: Array<SocialUniverseChannel>,
  t: TFunction<'app'>,
): string | null {
  if (!brand.id) {
    return null
  }
  const parts = SOCIAL_UNIVERSE_PLATFORM_ORDER.flatMap((platform) => {
    const channel = channels.find((entry) => entry.platform === platform)
    const coverage = channel?.brandCoverage?.[brand.id as string]
    if (!channel || !coverage) {
      return []
    }
    const label = SOCIAL_PLATFORM_LABELS[platform]
    const routes = coverage.routes.map((route) => t(`socialListening.topicDialog.coverageRoute.${route}`))
    const skipped = coverage.skipped.map((skip) =>
      t('socialListening.topicDialog.coverageSkipped', {
        route: t(`socialListening.topicDialog.coverageRoute.${skip.route}`),
        reason: t(`socialListening.topicDialog.coverageSkipReason.${skip.reason}`, { platform: label }),
      }),
    )
    const readDays = new Set(
      Object.values(channel.brandCoverage ?? {}).map((entry) => entry.readDays),
    )
    const days =
      readDays.size > 1 && coverage.readDays != null
        ? [t('socialListening.topicDialog.coverageDays', { count: coverage.readDays })]
        : []
    return [
      t('socialListening.topicDialog.coverageChannel', {
        platform: label,
        details: [
          ...(routes.length
            ? [t('socialListening.topicDialog.coverageAccepted', {
                routes: routes.join(', '),
                count: coverage.accepted,
              })]
            : []),
          ...skipped,
          ...days,
        ].join('; '),
      }),
    ]
  })
  return parts.length ? parts.join(' · ') : null
}

interface ReviewRow {
  key: string
  label: string
  value: string
}

interface ReviewSection {
  step: UniverseStep
  rows: Array<ReviewRow>
}

/** Every choice of the Topic, grouped by step (Topic, Brands, Channels, Tracking), each group one click from its step. */
export function SocialUniverseReview(props: SocialUniverseReviewProps) {
  const { form, channels, languageLabel, onEdit } = props

  const { t, i18n } = useTranslation('app')

  const none = t('socialListening.topicDialog.reviewNone')
  const list = (values: Array<string>) => (values.length ? values.join(', ') : none)
  const themes = form.topics
    .map((topic) => topic.name.trim())
    .filter((name) => name !== '')

  const sections: Array<ReviewSection> = [
    {
      step: 'topic',
      rows: [
        { key: 'name', label: t('socialListening.topicDialog.name'), value: form.name.trim() || none },
        // In step 1's order (D21b): what Suggest reads (goal, market, languages).
        ...(form.goal.trim()
          ? [{ key: 'goal', label: t('socialListening.topicDialog.about'), value: form.goal.trim() }]
          : []),
        {
          key: 'market',
          label: t('socialListening.topicDialog.market'),
          value: form.market
            ? localizedCountryName(form.marketCode ?? form.market, i18n.language)
            : none,
        },
        {
          key: 'languages',
          label: t('socialListening.topicDialog.languages'),
          value: list(form.languages.map(languageLabel)),
        },
      ],
    },
    {
      step: 'brands',
      rows: [
        // In step 2's order: what Suggest fills, each editable there.
        { key: 'keywords', label: t('socialListening.topicDialog.keywords'), value: list(form.keywords) },
        {
          key: 'subject-kind',
          label: t('socialListening.topicDialog.subjectKind'),
          value: t(`socialListening.topicDialog.subjectKinds.${form.subjectKind}`),
        },
        // Optional: shown only when filled in.
        ...(form.brief.include.trim()
          ? [{ key: 'about', label: t('socialListening.form.briefInclude'), value: form.brief.include.trim() }]
          : []),
        ...(form.brands.length
          ? []
          : [{ key: 'brands', label: t('socialListening.topicDialog.brands'), value: none }]),
        // Per brand (D20): its role, what it is searched as, its hashtags and accounts, each account confirmed or not.
        ...form.brands.map((brand, index) => ({
          key: `brand-${index}`,
          label: brand.role
            ? `${brand.name} · ${t(`socialListening.topicDialog.brandRoles.${brand.role}`)}`
            : brand.name,
          value: [
            brand.collect
              ? t('socialListening.topicDialog.reviewBrandSearches', {
                  searches: brandSearchQueries(brand).map((search) => `"${search.query}"`).join(', '),
                })
              : t('socialListening.topicDialog.reviewBrandNotSearched', { name: brand.name }),
            ...((brand.hashtags ?? []).length
              ? [(brand.hashtags ?? []).map((hashtag) => `#${hashtag.tag}`).join(', ')]
              : []),
            ...SOCIAL_UNIVERSE_PLATFORM_ORDER.flatMap((platform) => {
              const account = brand.accounts?.[platform]
              return account
                ? [
                    t(
                      account.confirmed
                        ? 'socialListening.topicDialog.reviewAccountConfirmed'
                        : 'socialListening.topicDialog.reviewAccountNotConfirmed',
                      { handle: account.handle, platform: SOCIAL_PLATFORM_LABELS[platform] },
                    ),
                  ]
                : []
            }),
          ].join(' · '),
        })),
        // Each brand's coverage on the saved channels, once a run stated it.
        ...form.brands.flatMap((brand, index) => {
          const line = socialBrandCoverageLine(brand, channels, t)
          return line
            ? [
                {
                  key: `coverage-${index}`,
                  label: t('socialListening.topicDialog.reviewCoverage', { name: brand.name }),
                  value: line,
                },
              ]
            : []
        }),
      ],
    },
    {
      step: 'channels',
      rows: [
        {
          key: 'channels',
          label: t('socialListening.topicDialog.channels'),
          value: list(
            SOCIAL_UNIVERSE_PLATFORM_ORDER.filter((platform) =>
              form.platforms.includes(platform),
            ).map((platform) => {
              // A narrowing community limits the channel to it; accounts add to its search.
              const narrow = communityNames(form, platform, 'narrow')
              if (narrow.length) {
                return t('socialListening.topicDialog.reviewOnlyCommunity', {
                  platform: SOCIAL_PLATFORM_LABELS[platform],
                  handles: narrow.join(', '),
                })
              }
              return form.handles[platform].length
                ? t('socialListening.topicDialog.reviewAlsoAccounts', {
                    platform: SOCIAL_PLATFORM_LABELS[platform],
                    handles: form.handles[platform].join(', '),
                  })
                : SOCIAL_PLATFORM_LABELS[platform]
            }),
          ),
        },
        // What each picked channel searches for, then the brands it also searches.
        ...SOCIAL_UNIVERSE_PLATFORM_ORDER.filter((platform) =>
          form.platforms.includes(platform),
        ).map((platform) => {
          const brands = channelBrandNames(form, platform)
          return {
            key: `searches-${platform}`,
            label: t('socialListening.topicDialog.reviewSearches', {
              platform: SOCIAL_PLATFORM_LABELS[platform],
            }),
            value: [
              list(form.searches[platform].map((search) => search.query)),
              ...(brands.length
                ? [
                    t('socialListening.topicDialog.channelBrands', {
                      count: brands.length,
                      names: brands.join(', '),
                    }),
                  ]
                : []),
            ].join(' · '),
          }
        }),
        // Hashtags and added communities, on the channels that have them.
        ...SOCIAL_UNIVERSE_PLATFORM_ORDER.filter(
          (platform) => form.platforms.includes(platform) && form.hashtags[platform].length > 0,
        ).map((platform) => ({
          key: `hashtags-${platform}`,
          label: t('socialListening.topicDialog.reviewHashtags', {
            platform: SOCIAL_PLATFORM_LABELS[platform],
          }),
          value: list(form.hashtags[platform].map((hashtag) => `#${hashtag.tag}`)),
        })),
        ...SOCIAL_UNIVERSE_PLATFORM_ORDER.filter(
          (platform) =>
            form.platforms.includes(platform) && communityNames(form, platform, 'add').length > 0,
        ).map((platform) => ({
          key: `communities-${platform}`,
          label: t('socialListening.topicDialog.reviewCommunities', {
            platform: SOCIAL_PLATFORM_LABELS[platform],
          }),
          value: list(communityNames(form, platform, 'add')),
        })),
      ],
    },
    {
      step: 'tracking',
      rows: [
        {
          key: 'depth',
          label: t('socialListening.form.firstCollection'),
          value: socialDepthOptionLabel(t, form.depthDays),
        },
        {
          key: 'first-limit',
          label: t('socialListening.form.firstCollectionLimitLabel'),
          value: t('socialListening.topicDialog.reviewFirstCollectionLimit', {
            value: form.firstCollectionPostLimit.toLocaleString(i18n.language),
          }),
        },
        {
          key: 'cadence',
          label: t('socialListening.form.then'),
          value: t(`socialListening.form.cadence.${form.cadence}`),
        },
        {
          key: 'limit',
          label: t('socialListening.form.weeklyRateLabel'),
          value: t('socialListening.topicDialog.reviewWeeklyRate', {
            value: form.runPostLimit,
          }),
        },
        {
          key: 'themes',
          label: t('socialListening.topicDialog.themes'),
          value: themes.length ? themes.join(', ') : t('socialListening.topicDialog.themesEmpty'),
        },
      ],
    },
  ]

  return (
    <div className="flex flex-col gap-3" data-testid="social-universe-review">
      {sections.map((section) => {
        const stepName = t(`socialListening.topicDialog.stepNames.${section.step}`)
        return (
          <section
            key={section.step}
            className="rounded-xl border border-border p-4"
            aria-labelledby={`social-universe-review-${section.step}-title`}
            data-testid={`social-universe-review-section-${section.step}`}
          >
            <div className="mb-3 flex items-center justify-between gap-3">
              <Text
                as="span"
                size="sm"
                className="font-medium"
                id={`social-universe-review-${section.step}-title`}
              >
                {stepName}
              </Text>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-auto gap-1.5 px-2 py-1 text-muted-foreground"
                aria-label={t('socialListening.topicDialog.editStepAria', { step: stepName })}
                data-testid={`social-universe-review-edit-${section.step}`}
                onClick={() => onEdit(section.step)}
              >
                <Pencil className="h-3 w-3" aria-hidden="true" />
                {t('socialListening.topicDialog.editStep')}
              </Button>
            </div>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-[9rem_minmax(0,1fr)]">
              {section.rows.map((row) => (
                <div key={row.key} className="contents">
                  <dt className="text-muted-foreground">{row.label}</dt>
                  <dd
                    className="mb-1 break-words text-foreground sm:mb-0"
                    data-testid={`social-universe-review-${row.key}`}
                  >
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        )
      })}
    </div>
  )
}
